import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";

import { useTouchListPickSession } from "@/lib/useTouchListPickSession";
import { cn } from "@/lib/utils";
import { registerViewportLayoutListeners } from "@/lib/useViewportLayoutEvents";
import {
  computeAnchoredListPanel,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredListPanelBox,
} from "@/lib/viewportAnchoredPopover";
import { Input } from "@/components/ui/Input";
import {
  isSearchableSelectDivider,
  SearchableSelectDividerRow,
  searchableSelectSelectableOptions,
  type SearchableSelectOption,
} from "@/components/ui/SearchableSelect";

const LIST_MAX_HEIGHT_PX = 320;
const PANEL_GAP_PX = 4;
const VIEWPORT_EDGE_PX = 8;
const PANEL_SEARCH_CHROME_PX = 48;

function getScrollParents(node: HTMLElement | null): HTMLElement[] {
  const parents: HTMLElement[] = [];
  let element = node?.parentElement;
  while (element) {
    const style = window.getComputedStyle(element);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const overflow = style.overflow;
    if (
      /(auto|scroll|overlay)/.test(overflowY) ||
      /(auto|scroll|overlay)/.test(overflowX) ||
      /(auto|scroll|overlay)/.test(overflow)
    ) {
      parents.push(element);
    }
    element = element.parentElement;
  }
  return parents;
}

export interface SearchableMultiSelectProps {
  id?: string;
  selectedValues: ReadonlySet<string>;
  onSelectedValuesChange: (next: Set<string>) => void;
  options: SearchableSelectOption[];
  searchValue: string;
  onSearchChange: (query: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  panelMinWidth?: number;
  /** Trigger label when every option is selected (default selection mode only). */
  allSelectedLabel?: string;
  /** Trigger label when nothing is selected (default selection mode only). */
  noneSelectedLabel?: string;
  /**
   * When true, an empty selection means “no filter” (trigger shows {@link SearchableMultiSelectProps.placeholder}).
   * Checked options restrict the result set. Forecast supplier filters keep the default false.
   */
  emptySelectionMeansAll?: boolean;
  /** When set, always show this label on the trigger instead of the computed selection summary. */
  fixedTriggerLabel?: string;
  /** Inline chips with a compact add control (default), or a single-line summary trigger. */
  variant?: "chips" | "compact";
  /** Label on the add control when `variant` is `chips`. */
  addLabel?: string;
  /** Custom chip renderer for `variant="chips"` (default: text label + remove). */
  renderChip?: (props: {
    option: SearchableSelectOption;
    disabled: boolean;
    onRemove: () => void;
  }) => ReactNode;
  /**
   * Keep selected chips inside the field even when every loaded option is selected.
   * Use for entity pickers; leave false for filter widgets that collapse to "All".
   */
  alwaysShowChips?: boolean;
  /** Cap how many values can be selected. `1` replaces the current value (single picker). */
  maxSelected?: number;
}

export function getSearchableMultiSelectSelectedOptions(
  selectedValues: ReadonlySet<string>,
  options: readonly SearchableSelectOption[],
): SearchableSelectOption[] {
  const selected = options.filter((option) => selectedValues.has(option.value));
  const present = new Set(selected.map((option) => option.value));
  for (const value of selectedValues) {
    if (!present.has(value)) {
      selected.push({ value, label: value });
    }
  }
  return selected;
}

export function applySearchableMultiSelectToggle(
  selectedValues: ReadonlySet<string>,
  value: string,
  maxSelected?: number,
): Set<string> {
  const next = new Set(selectedValues);
  if (next.has(value)) {
    next.delete(value);
    return next;
  }
  if (maxSelected === 1) {
    return new Set([value]);
  }
  if (maxSelected != null && next.size >= maxSelected) {
    return next;
  }
  next.add(value);
  return next;
}

export function applySearchableMultiSelectAdd(
  selectedValues: ReadonlySet<string>,
  value: string,
  maxSelected?: number,
): Set<string> {
  if (selectedValues.has(value)) {
    return new Set(selectedValues);
  }
  return applySearchableMultiSelectToggle(selectedValues, value, maxSelected);
}

export function formatSearchableMultiSelectTriggerLabel(
  selectedValues: ReadonlySet<string>,
  options: readonly SearchableSelectOption[],
  labels?: {
    allSelectedLabel?: string;
    noneSelectedLabel?: string;
    placeholder?: string;
    emptySelectionMeansAll?: boolean;
  },
): string {
  const placeholder = labels?.placeholder ?? "Select…";
  const allSelectedLabel = labels?.allSelectedLabel ?? "All";
  const noneSelectedLabel = labels?.noneSelectedLabel ?? "None selected";
  const emptySelectionMeansAll = labels?.emptySelectionMeansAll === true;
  const selectableCount = searchableSelectSelectableOptions(options).length;
  if (selectableCount === 0) return placeholder;
  if (emptySelectionMeansAll) {
    if (selectedValues.size === 0) return placeholder;
    if (selectedValues.size >= selectableCount) return placeholder;
  } else {
    if (selectedValues.size === 0) return noneSelectedLabel;
    if (selectedValues.size >= selectableCount) return allSelectedLabel;
  }
  if (selectedValues.size === 1) {
    const onlyValue = [...selectedValues][0];
    const match = options.find((option) => option.value === onlyValue);
    return match?.label ?? onlyValue;
  }
  const selectedLabels = getSearchableMultiSelectSelectedOptions(selectedValues, options).map(
    (option) => option.label,
  );
  return selectedLabels.join(", ");
}

/** When the panel search field has filter text, commit the highlighted or first filtered option (Enter key). */
export function pickSearchableMultiSelectOptionFromSearch(
  filteredOptions: SearchableSelectOption[],
  searchValue: string,
  highlightedIndex?: number | null,
): SearchableSelectOption | null {
  const needleRaw = searchValue.trim();
  if (needleRaw.length === 0 || filteredOptions.length === 0) return null;

  if (
    highlightedIndex != null &&
    highlightedIndex >= 0 &&
    highlightedIndex < filteredOptions.length
  ) {
    const highlighted = filteredOptions[highlightedIndex];
    if (highlighted != null && !isSearchableSelectDivider(highlighted)) return highlighted;
  }

  const selectable = searchableSelectSelectableOptions(filteredOptions);
  if (selectable.length === 1) return selectable[0];

  const exactLabel = selectable.filter(
    (option) => option.label.trim().toLowerCase() === needleRaw.toLowerCase(),
  );
  if (exactLabel.length === 1) return exactLabel[0];

  const byValue = selectable.filter((option) => option.value === needleRaw);
  if (byValue.length === 1) return byValue[0];

  return selectable[0] ?? null;
}

function SearchableMultiSelectOptionChip({
  option,
  disabled,
  onRemove,
}: {
  option: SearchableSelectOption;
  disabled: boolean;
  onRemove: () => void;
}): JSX.Element {
  return (
    <span
      className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800"
      title={option.label}
    >
      <span className="truncate">{option.label}</span>
      <button
        type="button"
        className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
        aria-label={`Remove ${option.label}`}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  );
}

export function SearchableMultiSelect({
  id: providedId,
  selectedValues,
  onSelectedValuesChange,
  options,
  searchValue,
  onSearchChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No matches.",
  isLoading = false,
  loadingMessage = "Searching…",
  disabled = false,
  className,
  triggerClassName,
  panelMinWidth = 120,
  allSelectedLabel = "All",
  noneSelectedLabel = "None selected",
  emptySelectionMeansAll = false,
  fixedTriggerLabel,
  variant = "chips",
  addLabel: _addLabel = "Add…",
  renderChip,
  alwaysShowChips = false,
  maxSelected,
}: SearchableMultiSelectProps): JSX.Element {
  const generatedId = useId();
  const triggerId = providedId ?? `${generatedId}-trigger`;
  const listboxId = providedId ? `${providedId}-listbox` : `${generatedId}-listbox`;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const touchListPick = useTouchListPickSession(open);
  const suppressCheckboxChangeRef = useRef(false);
  const [panelBox, setPanelBox] = useState<AnchoredListPanelBox | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  const filteredOptions = useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    if (needle.length === 0) return options;
    return options.filter((option) => {
      const label = option.label.toLowerCase();
      const value = option.value.toLowerCase();
      const description = (option.description ?? "").toLowerCase();
      return label.includes(needle) || value.includes(needle) || description.includes(needle);
    });
  }, [options, searchValue]);

  const triggerLabel =
    fixedTriggerLabel ??
    formatSearchableMultiSelectTriggerLabel(selectedValues, options, {
      placeholder,
      allSelectedLabel,
      noneSelectedLabel,
      emptySelectionMeansAll,
    });

  const selectedOptions = useMemo(
    () => getSearchableMultiSelectSelectedOptions(selectedValues, options),
    [options, selectedValues],
  );

  const collapsedToAllLabel =
    !alwaysShowChips &&
    options.length > 0 &&
    selectedValues.size >= options.length;

  const showInlineSelectedChips =
    variant === "chips" &&
    fixedTriggerLabel == null &&
    selectedOptions.length > 0 &&
    !collapsedToAllLabel;

  const inlineTriggerLabel =
    fixedTriggerLabel ?? (showInlineSelectedChips ? "" : triggerLabel);
  const inlineTriggerIsPlaceholder =
    inlineTriggerLabel === placeholder ||
    inlineTriggerLabel === noneSelectedLabel ||
    (inlineTriggerLabel === allSelectedLabel && !emptySelectionMeansAll);

  const updatePanelPosition = (): void => {
    const anchor =
      variant === "chips" ? fieldRef.current ?? triggerRef.current : triggerRef.current;
    if (anchor == null) return;
    setPanelBox(
      computeAnchoredListPanel({
        anchorRect: anchor.getBoundingClientRect(),
        gap: PANEL_GAP_PX,
        edge: VIEWPORT_EDGE_PX,
        minWidth: panelMinWidth,
        chromeHeight: PANEL_SEARCH_CHROME_PX,
        listMaxHeightCap: LIST_MAX_HEIGHT_PX,
      }),
    );
  };

  useLayoutEffect(() => {
    if (!open) {
      setPanelBox(null);
      return;
    }
    updatePanelPosition();
    const onWin = (): void => {
      updatePanelPosition();
    };
    const removeViewport = registerViewportLayoutListeners(onWin);
    const scrollAnchor =
      variant === "chips" ? fieldRef.current ?? triggerRef.current : triggerRef.current;
    const scrollParents = getScrollParents(scrollAnchor);
    for (const parent of scrollParents) {
      parent.addEventListener("scroll", onWin, { passive: true });
    }
    return () => {
      removeViewport();
      for (const parent of scrollParents) {
        parent.removeEventListener("scroll", onWin);
      }
    };
  }, [open, options.length, searchValue, variant]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setHighlightedIndex(null);
      return;
    }
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : null);
  }, [open, searchValue, filteredOptions]);

  const refocusSearchInput = (): void => {
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  };

  const toggleValue = (value: string): void => {
    onSelectedValuesChange(applySearchableMultiSelectToggle(selectedValues, value, maxSelected));
    refocusSearchInput();
  };

  const removeValue = (value: string): void => {
    const next = new Set(selectedValues);
    next.delete(value);
    onSelectedValuesChange(next);
  };

  const selectAll = (): void => {
    onSelectedValuesChange(new Set(searchableSelectSelectableOptions(options).map((option) => option.value)));
    refocusSearchInput();
  };

  const clearAll = (): void => {
    onSelectedValuesChange(new Set());
    refocusSearchInput();
  };

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  const openPicker = (): void => {
    if (disabled) return;
    setOpen((current) => !current);
  };

  const addValueFromSearch = (option: SearchableSelectOption): void => {
    onSelectedValuesChange(applySearchableMultiSelectAdd(selectedValues, option.value, maxSelected));
    onSearchChange("");
    setHighlightedIndex(null);
    refocusSearchInput();
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown") {
      if (filteredOptions.length === 0) return;
      event.preventDefault();
      setHighlightedIndex((current) => {
        if (current == null) return 0;
        return Math.min(current + 1, filteredOptions.length - 1);
      });
      return;
    }
    if (event.key === "ArrowUp") {
      if (filteredOptions.length === 0) return;
      event.preventDefault();
      setHighlightedIndex((current) => {
        if (current == null) return filteredOptions.length - 1;
        return Math.max(current - 1, 0);
      });
      return;
    }
    if (event.key !== "Enter") return;
    event.preventDefault();
    const picked = pickSearchableMultiSelectOptionFromSearch(
      filteredOptions,
      searchValue,
      highlightedIndex,
    );
    if (picked == null) return;
    addValueFromSearch(picked);
  };

  const listPanel =
    open && panelBox != null && portalTarget
      ? createPortal(
          <div
            ref={panelRef}
            id={listboxId}
            role="listbox"
            aria-multiselectable={maxSelected === 1 ? "false" : "true"}
            data-searchable-select-panel
            style={{
              position: "fixed",
              top: panelBox.top,
              left: panelBox.left,
              width: panelBox.width,
              zIndex: FLOATING_POPOVER_Z_INDEX,
            }}
            className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
          >
            <div className="space-y-2 border-b border-slate-100 p-2">
              <Input
                ref={searchInputRef}
                value={searchValue}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="h-8 text-sm"
                onKeyDown={handleSearchKeyDown}
              />
              <div className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-3">
                  {maxSelected == null ? (
                    <button
                      type="button"
                      className="text-brand-700 hover:underline"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={selectAll}
                    >
                      Select all
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="text-brand-700 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={clearAll}
                    disabled={selectedValues.size === 0}
                  >
                    Clear all
                  </button>
                </div>
                {selectedValues.size > 0 &&
                (!emptySelectionMeansAll || selectedValues.size < options.length) ? (
                  <span className="text-slate-500">{selectedValues.size} selected</span>
                ) : null}
              </div>
            </div>
            <ul
              className="overflow-auto py-1 text-sm"
              style={{ maxHeight: panelBox.listContentMaxHeight }}
            >
              {isLoading ? (
                <li className="px-3 py-2 text-slate-500">{loadingMessage}</li>
              ) : options.length === 0 ? (
                <li className="px-3 py-2 text-slate-500">{emptyMessage}</li>
              ) : filteredOptions.length === 0 ? (
                <li className="px-3 py-2 text-slate-500">{emptyMessage}</li>
              ) : (
                filteredOptions.map((option, optionIndex) => {
                  if (isSearchableSelectDivider(option)) {
                    return <SearchableSelectDividerRow key={option.value} label={option.label} />;
                  }
                  const isSelected = selectedValues.has(option.value);
                  const isHighlighted = optionIndex === highlightedIndex;
                  return (
                    <li key={option.value} role="presentation">
                      <label
                        className={cn(
                          "flex cursor-pointer items-start gap-2 px-3 py-2 hover:bg-slate-100",
                          isHighlighted && "bg-slate-100",
                        )}
                        onMouseDown={(event) => event.preventDefault()}
                        onPointerDown={(event) => {
                          if (event.pointerType === "mouse") return;
                          touchListPick.onListItemPointerDown(event, () => {
                            suppressCheckboxChangeRef.current = true;
                            toggleValue(option.value);
                          });
                        }}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                          checked={isSelected}
                          onChange={() => {
                            if (suppressCheckboxChangeRef.current) {
                              suppressCheckboxChangeRef.current = false;
                              return;
                            }
                            toggleValue(option.value);
                          }}
                          tabIndex={-1}
                        />
                        <span className="flex min-w-0 flex-col gap-0.5 text-left">
                          <span className="whitespace-nowrap">{option.label}</span>
                          {option.description ? (
                            <span className="whitespace-normal text-xs leading-snug text-slate-500">
                              {option.description}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          portalTarget,
        )
      : null;

  const compactTriggerClassName = cn(
    "inline-flex h-10 w-full items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 text-left text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
    disabled ? "cursor-not-allowed bg-slate-100 text-slate-500" : "border-slate-300 bg-white text-slate-900",
    triggerClassName,
  );

  const chipsInlineTriggerClassName = cn(
    "min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-1 text-left text-sm focus:outline-none",
    disabled ? "cursor-not-allowed" : "cursor-text",
    inlineTriggerIsPlaceholder ? "text-slate-400" : "text-slate-900",
    triggerClassName,
  );

  const renderOptionChip = (
    option: SearchableSelectOption,
    onRemove: () => void,
  ): ReactNode => {
    if (renderChip != null) {
      return renderChip({ option, disabled, onRemove });
    }
    return (
      <SearchableMultiSelectOptionChip
        option={option}
        disabled={disabled}
        onRemove={onRemove}
      />
    );
  };

  if (variant === "chips" && fixedTriggerLabel == null) {
    return (
      <div ref={containerRef} className={cn("relative", className)}>
        <div
          ref={fieldRef}
          className={cn(
            "flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900",
            "focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500",
            disabled && "cursor-not-allowed bg-slate-100",
          )}
          onClick={() => {
            if (!disabled) {
              openPicker();
            }
          }}
        >
          {showInlineSelectedChips
            ? selectedOptions.map((option) => (
                <span key={option.value} className="inline-flex max-w-full">
                  {renderOptionChip(option, () => removeValue(option.value))}
                </span>
              ))
            : null}
          <button
            ref={triggerRef}
            id={triggerId}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            onClick={(event) => {
              event.stopPropagation();
              openPicker();
            }}
            className={chipsInlineTriggerClassName}
          >
            {inlineTriggerLabel.length > 0 ? inlineTriggerLabel : "\u00a0"}
          </button>
        </div>
        {listPanel}
      </div>
    );
  }

  if (variant === "chips" && fixedTriggerLabel != null) {
    return (
      <div ref={containerRef} className={cn("relative", className)}>
        <div
          ref={fieldRef}
          className={cn(
            "flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900",
            "focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500",
            disabled && "cursor-not-allowed bg-slate-100",
          )}
          onClick={() => {
            if (!disabled) {
              openPicker();
            }
          }}
        >
          <button
            ref={triggerRef}
            id={triggerId}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            onClick={(event) => {
              event.stopPropagation();
              openPicker();
            }}
            className={chipsInlineTriggerClassName}
          >
            {fixedTriggerLabel}
          </button>
        </div>
        {listPanel}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={openPicker}
        className={compactTriggerClassName}
      >
        <span className={cn("min-w-0 flex-1 truncate", triggerLabel === placeholder && "text-slate-400")}>
          {triggerLabel}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
      </button>
      {listPanel}
    </div>
  );
}
